import { z } from 'zod';
import {
  CALL_CRITERION_LIMITS,
  CallCriterionOptionSchema,
  CallCriterionSchema,
  type CallCriterion,
} from './definition';
import { builtinTakesWordList } from './builtin-catalog';

/**
 * Критерий в том виде, в каком он хранится (таблица `call_criteria`, #857), и
 * правила его правки. Определение для шага оценки (`CallCriterion`) — проекция
 * хранимого: архивные варианты модель не видит.
 */

/**
 * Вариант списка в хранилище. Коды неизменяемы — на них ссылаются накопленные
 * результаты, — поэтому вариант не удаляют, а архивируют (спека §3.1).
 */
export const StoredCallCriterionOptionSchema = CallCriterionOptionSchema.extend({
  archived: z.boolean(),
});
export type StoredCallCriterionOption = z.infer<typeof StoredCallCriterionOptionSchema>;

export const CALL_CRITERION_CHANNELS = ['call', 'chat'] as const;

/**
 * Сколько встроенных в каталоге (`builtin-catalog.ts`) — для потолка
 * организации (лимит тарифа + каталог, спека §5). Отдельным числом, а не
 * `BUILTIN_CRITERIA_CATALOG.length`: браузеру нужно только оно, а импорт
 * каталога унёс бы в бандл тексты встроенных, которые API от клиента прячет
 * (локальный Codex на #1563). Совпадение с каталогом держит тест каталога.
 */
export const BUILTIN_CRITERIA_COUNT = 8;

/**
 * Потолок неархивных критериев организации (спека §5, решение В6): лимит
 * тарифа для собственных плюс каталог встроенных — встроенные не отнимают
 * места у собственных. Одно правило на сервер (`criteriaCeiling` в edge) и
 * экран критериев: разойдись они, кнопка «Новый критерий» обещала бы место,
 * которого сервер не даст. Сумма по самому большому тарифу ограничена
 * замеренной strict-схемой ответа (37 свойств, Э0) — это держит тест тарифов.
 */
export function criteriaCeilingFor(customLimit: number): number {
  return customLimit + BUILTIN_CRITERIA_COUNT;
}

/**
 * Направление звонка — четвёртое измерение области (#868, Э1). Ограничивает
 * ТОЛЬКО звонки: у чата направления нет, и критерий с каналом «чат» чаты
 * оценивает независимо от направления; звонок без направления в критерий с
 * ограничением по направлению не попадает.
 */
export const CALL_CRITERION_DIRECTIONS = ['inbound', 'outbound'] as const;
export type CallCriterionDirection = (typeof CALL_CRITERION_DIRECTIONS)[number];

/**
 * Значение колонки направления (D1 `records_metadata.call_direction`, PG
 * `records.call_direction` — свободный текст) → направление области. Только
 * точное совпадение, без приведения регистра: отчёт «не оценено» в Postgres
 * сравнивает колонку как есть, и `'OUTBOUND'` там в список не входит — здесь
 * тоже. Всё прочее — «неизвестно» (`null`).
 */
export function toCallCriterionDirection(raw: unknown): CallCriterionDirection | null {
  return (CALL_CRITERION_DIRECTIONS as readonly unknown[]).includes(raw)
    ? (raw as CallCriterionDirection)
    : null;
}

/**
 * Кто поставил критерий на паузу. Сверка встроенных снимает только свои
 * причины (`profile`, `jurisdiction`) и никогда — паузу клиента (#868, §6).
 *
 * `no_words` — у встроенного `forbidden_words` пуст список (#2784): оценивать
 * нечего. Её ставит и снимает правка списка (маршрут критериев), а не сверка:
 * сверка, прочитавшая строку до сохранения списка, вернула бы паузу поверх
 * заполненного. Поэтому она не пауза Aelo — сверка её только сохраняет.
 */
export const CALL_CRITERION_PAUSE_REASONS = [
  'customer',
  'profile',
  'jurisdiction',
  'catalog',
  'no_words',
] as const;
export type CallCriterionPauseReason = (typeof CALL_CRITERION_PAUSE_REASONS)[number];

/**
 * Пауза Aelo: её ставит и снимает только сверка встроенных, клиент её не
 * снимает (#868, §6), и при Э2 она одна из пауз, которыми сверка ещё владеет
 * (#2520). Одно определение на сверку, запись (`upsertBuiltin`), маршрут
 * критериев и экран.
 */
export const AELO_PAUSE_REASONS = [
  'jurisdiction',
  'catalog',
] as const satisfies readonly CallCriterionPauseReason[];
export type AeloPauseReason = (typeof AELO_PAUSE_REASONS)[number];

export function isAeloPauseReason(
  reason: string | null | undefined
): reason is AeloPauseReason {
  return (AELO_PAUSE_REASONS as readonly string[]).includes(reason ?? '');
}

const distinct = (values: readonly string[]): boolean =>
  new Set(values).size === values.length;

/**
 * Непустой массив без повторов или `null` («без ограничения»). Пустой массив
 * отвергается: «нигде» — это пауза критерия, а не пустая область.
 */
const scopeList = <T extends z.ZodType<string>>(item: T) =>
  z.array(item).min(1).refine(distinct, 'значения не повторяются').nullable();

/**
 * Область действия (Р3, Р9). Категории здесь — просто строки: их перечень
 * (`analyzedCallCategorySchema`) живёт в `@aica/api-types`, и проверяет его
 * слой API, а не core.
 */
export const CallCriterionScopeSchema = z.object({
  categories: scopeList(z.string().min(1)),
  projectIds: scopeList(z.string().min(1)),
  channels: scopeList(z.enum(CALL_CRITERION_CHANNELS)),
  /**
   * Необязательное поле, а не обязательное `null`: области собирают и те, кто
   * о направлении ещё не знает (конструктор UI до PR 3b плана Э1, тесты), и
   * отсутствие значит то же, что `null`, — «без ограничения». Хранилище
   * всегда отдаёт его явно.
   */
  directions: scopeList(z.enum(CALL_CRITERION_DIRECTIONS)).optional(),
});
export type CallCriterionScope = z.infer<typeof CallCriterionScopeSchema>;

export interface StoredCallCriterionDefinition {
  name: string;
  question: string;
  guidance: string | null;
  resultType: CallCriterion['resultType'];
  looksFor: 'presence' | 'absence' | null;
  options: StoredCallCriterionOption[] | null;
  /**
   * Список слов встроенного `forbidden_words` (#2784); `null` — списка нет.
   * Необязательный: определения собственных критериев его не знают.
   */
  wordList?: string[] | null;
}

/** «Без ограничения» по всем осям — область по умолчанию. */
export const NO_CRITERION_SCOPE: CallCriterionScope = {
  categories: null,
  projectIds: null,
  channels: null,
  directions: null,
};

/**
 * Запрос на создание критерия → хранимое определение.
 *
 * Живёт здесь, а не в маршруте, потому что потребителей ДВА: сохранение
 * критерия (edge-api) и проверка формулировки на выборке (#861, workflows-api).
 * У «проверить» и «сохранить» не должно быть двух представлений о допустимом
 * критерии — общей схемы запроса для этого мало: четыре решения ниже (`guidance`
 * пустой строкой значит «нет», `looksFor` только у флажка, варианты только у
 * списка и все неархивные, область по умолчанию) и есть само представление, и
 * разъехавшись, они заставили бы проверку отвечать по одному определению, а
 * сохранение — писать другое (`/code-review`).
 *
 * Аргумент типизован структурно: `@aica/core` не зависит от `@aica/api-types`,
 * где живёт схема запроса, и зависеть не должен.
 */
interface CriterionRequestCommon {
  name: string;
  question: string;
  guidance?: string | null | undefined;
}

/**
 * Запрос на создание — РАЗМЕЧЕННОЕ объединение, как и схема, из которой он
 * приходит.
 *
 * Структурный «мешок» (`resultType` рядом с необязательными `looksFor` и
 * `options`) позволял бы собрать `{ resultType: 'choice', options: undefined }`:
 * компилируется, даёт `options: null`, а `definitionForEvaluation` объявляет
 * такой критерий неоценимым — сохранённый критерий, который никогда не
 * оценивается, с одной строкой в логе. Объединение делает это ошибкой типа
 * (`/code-review`).
 */
export type CriterionDefinitionRequest = CriterionRequestCommon &
  (
    | { resultType: 'flag'; looksFor: 'presence' | 'absence' }
    | { resultType: 'score' }
    | {
        resultType: 'choice';
        options: readonly { code: string; label: string; needsEvidence: boolean }[];
      }
  );

export function storedDefinitionFromRequest(
  request: CriterionDefinitionRequest
): StoredCallCriterionDefinition {
  const base = {
    name: request.name,
    question: request.question,
    // Пустая строка — это «подсказки нет», а не подсказка из нуля символов.
    guidance: request.guidance || null,
  };
  switch (request.resultType) {
    case 'flag':
      return { ...base, resultType: 'flag', looksFor: request.looksFor, options: null };
    case 'score':
      return { ...base, resultType: 'score', looksFor: null, options: null };
    case 'choice':
      return {
        ...base,
        resultType: 'choice',
        looksFor: null,
        // Архивировать при создании нечего: критерия ещё не существует.
        options: request.options.map((option) => ({ ...option, archived: false })),
      };
  }
}

export interface CallCriterionDefinitionPatch {
  name?: string;
  question?: string;
  /** `null` — стереть подсказку. */
  guidance?: string | null;
  /** Принимается только равным текущему: тип после создания не меняется. */
  resultType?: CallCriterion['resultType'];
  looksFor?: 'presence' | 'absence';
  /** Полный список вариантов: подписи, порядок, архив, новые коды. */
  options?: StoredCallCriterionOption[];
  /** Полный список слов; `null` — очистить. Какому критерию он разрешён, решает маршрут. */
  wordList?: string[] | null;
}

export type CriterionEditError =
  | 'result_type_immutable'
  | 'options_not_allowed'
  | 'looks_for_not_allowed'
  | 'word_list_not_allowed'
  | 'option_code_removed'
  | 'option_code_duplicate'
  | 'too_many_active_options'
  | 'no_active_options';

export type CriterionEditPlan =
  | { ok: true; next: StoredCallCriterionDefinition; semantic: boolean }
  | { ok: false; error: CriterionEditError };

function optionsError(
  before: readonly StoredCallCriterionOption[],
  after: readonly StoredCallCriterionOption[]
): CriterionEditError | null {
  const codes = after.map((o) => o.code);
  if (!distinct(codes)) return 'option_code_duplicate';
  // Перекодировать — это убрать старый код и добавить новый; ловится здесь же.
  if (before.some((o) => !codes.includes(o.code))) return 'option_code_removed';
  const active = after.filter((o) => !o.archived).length;
  if (active === 0) return 'no_active_options';
  // Потолок защищает промпт; архивных модель не видит, и в потолок они не входят.
  if (active > CALL_CRITERION_LIMITS.options) return 'too_many_active_options';
  return null;
}

/** Изменилось ли то, что видит модель: коды, требование цитаты, архив. */
function optionsChangedSemantically(
  before: readonly StoredCallCriterionOption[],
  after: readonly StoredCallCriterionOption[]
): boolean {
  if (before.length !== after.length) return true;
  const byCode = new Map(before.map((o) => [o.code, o]));
  return after.some((o) => {
    const was = byCode.get(o.code);
    return !was || was.needsEvidence !== o.needsEvidence || was.archived !== o.archived;
  });
}

/** Пустой список слов хранится как «списка нет». */
function emptyToNull(words: string[] | null): string[] | null {
  return words && words.length > 0 ? words : null;
}

/**
 * Тот же список для модели: порядок и регистр смысла не несут (повторы без
 * учёта регистра схема уже отбросила). Удалить слово и вернуть его в конец —
 * не новая формулировка.
 */
function sameWordList(a: string[] | null, b: string[] | null): boolean {
  const norm = (words: string[] | null) =>
    (words ?? []).map((word) => word.toLocaleLowerCase()).sort();
  return JSON.stringify(norm(a)) === JSON.stringify(norm(b));
}

/**
 * Допустима ли правка и поднимает ли она версию (спека §3.1).
 *
 * Смысловая правка — вопрос, подсказка, «что ищем», состав вариантов (код,
 * требование цитаты, архив): результаты до и после неё несравнимы. Имя и
 * подписи вариантов — нет. Тип ответа не меняется вовсе (план части 2,
 * отступление от буквы спеки): уход из списка уничтожил бы варианты, на коды
 * которых ссылаются накопленные результаты.
 */
export function planCriterionEdit(
  before: StoredCallCriterionDefinition,
  patch: CallCriterionDefinitionPatch
): CriterionEditPlan {
  if (patch.resultType !== undefined && patch.resultType !== before.resultType) {
    return { ok: false, error: 'result_type_immutable' };
  }
  if (patch.options !== undefined) {
    if (before.resultType !== 'choice')
      return { ok: false, error: 'options_not_allowed' };
    const error = optionsError(before.options ?? [], patch.options);
    if (error) return { ok: false, error };
  }
  if (patch.looksFor !== undefined && before.resultType !== 'flag') {
    return { ok: false, error: 'looks_for_not_allowed' };
  }
  if (patch.wordList !== undefined && before.resultType !== 'flag') {
    return { ok: false, error: 'word_list_not_allowed' };
  }

  const next: StoredCallCriterionDefinition = {
    ...before,
    ...(patch.name !== undefined && { name: patch.name }),
    ...(patch.question !== undefined && { question: patch.question }),
    ...(patch.guidance !== undefined && { guidance: patch.guidance }),
    ...(patch.looksFor !== undefined && { looksFor: patch.looksFor }),
    ...(patch.options !== undefined && { options: patch.options }),
    ...(patch.wordList !== undefined && { wordList: emptyToNull(patch.wordList) }),
  };
  // Список — часть того, что видит модель: ответы до и после правки
  // несравнимы, как после смены юрисдикции (#2784).
  const semantic =
    next.question !== before.question ||
    next.guidance !== before.guidance ||
    next.looksFor !== before.looksFor ||
    optionsChangedSemantically(before.options ?? [], next.options ?? []) ||
    !sameWordList(next.wordList ?? null, before.wordList ?? null);
  return { ok: true, next, semantic };
}

/**
 * Определение для шага оценки: хранимое без архивных вариантов, проверенное
 * `CallCriterionSchema`. Неоценимое определение (флажок без «что ищем», список
 * без неархивных вариантов, «запрещённые слова» без слов) — ошибка, а не
 * молчаливая подстановка: без списка модель угадывала бы, какие слова
 * запрещены (#2784).
 */
export function definitionForEvaluation(
  key: string,
  stored: StoredCallCriterionDefinition
): CallCriterion {
  const base = {
    key,
    name: stored.name,
    question: stored.question,
    ...(stored.guidance ? { guidance: stored.guidance } : {}),
  };
  switch (stored.resultType) {
    case 'flag':
      if (builtinTakesWordList(key) && !stored.wordList?.length) {
        throw new Error(`Criterion ${key} has an empty word list`);
      }
      return CallCriterionSchema.parse({
        ...base,
        resultType: 'flag',
        looksFor: stored.looksFor,
        ...(stored.wordList?.length ? { wordList: stored.wordList } : {}),
      });
    case 'score':
      return CallCriterionSchema.parse({ ...base, resultType: 'score' });
    case 'choice':
      return CallCriterionSchema.parse({
        ...base,
        resultType: 'choice',
        options: (stored.options ?? [])
          .filter((o) => !o.archived)
          .map(({ code, label, needsEvidence }) => ({ code, label, needsEvidence })),
      });
  }
}
